import React, { useState, useEffect, useRef} from 'react';
import { 
  Link, 
  ChevronRight,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Search,
  User,
  FileText,
  Filter,
  Loader2,
  ChevronDown,
  ChevronUp,
  Phone,
  Building2,
  Banknote,
  Zap
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast, { Toaster } from 'react-hot-toast';
import apiService from '../services/apiService';
import BaixaPix from './BaixaPix';
import BaixaPixVinculado from '../components/BaixaPixVinculado';
import AjudaConciliacao from '../components/AjudaConciliacao';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

export default function ConciliacaoPix() {
  const { usuario } = useAuth();
  const [params, setParams] = useSearchParams();
  const versaoConsulta = useRef(0);
  const podeBaixar = ['admin', 'gerente', 'caixa'].includes(usuario?.perfil);
  const abaBaixa = podeBaixar && params.get('aba') === 'baixa';

  // Estados principais
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({});

  // Filtros de busca
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  // Modal de vinculação
  const [modalVinculacao, setModalVinculacao] = useState(false);
  const [linkSelecionado, setLinkSelecionado] = useState(null);

  // Busca de cliente
  const [buscaCliente, setBuscaCliente] = useState('');
  const [tipoBusca, setTipoBusca] = useState('nome');
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [loadingBusca, setLoadingBusca] = useState(false);

  // Contas do cliente
  const [contasDisponiveis, setContasDisponiveis] = useState([]);
  const [contasExpandido, setContasExpandido] = useState(false);
  const [loadingContas, setLoadingContas] = useState(false);

  // Seleção de contas
  const [contasSelecionadas, setContasSelecionadas] = useState([]);
  const [observacoes, setObservacoes] = useState('');
  const [valorJuros, setValorJuros] = useState('');

  // Modal PIX Bancário
  const [modalPixBancario, setModalPixBancario] = useState(false);
  const [pbTipoBusca, setPbTipoBusca] = useState('nome');
  const [pbBusca, setPbBusca] = useState('');
  const [pbClientes, setPbClientes] = useState([]);
  const [pbClienteSelecionado, setPbClienteSelecionado] = useState(null);
  const [pbLoadingBusca, setPbLoadingBusca] = useState(false);
  const [pbTipoCobranca, setPbTipoCobranca] = useState('parcela');
  const [pbSequenciaVenda, setPbSequenciaVenda] = useState('');
  const [pbVendaSelecionada, setPbVendaSelecionada] = useState(null);
  const [pbLoadingVenda, setPbLoadingVenda] = useState(false);
  const [pbContas, setPbContas] = useState([]);
  const [pbContasSelecionadas, setPbContasSelecionadas] = useState([]);
  const [pbLoadingContas, setPbLoadingContas] = useState(false);
  const [pbValor, setPbValor] = useState('');
  const [pbPagadorNome, setPbPagadorNome] = useState('');
  const [pbPagadorCpf, setPbPagadorCpf] = useState('');
  const [pbDataPagamento, setPbDataPagamento] = useState('');
  const [pbObservacoes, setPbObservacoes] = useState('');
  const [pbHorarioDeposito, setPbHorarioDeposito] = useState('');
  const [pbSalvando, setPbSalvando] = useState(false);
  const [pbErro, setPbErro] = useState(null);
  const [pbValorJuros, setPbValorJuros] = useState('');
  const [pbTemJuros, setPbTemJuros] = useState(false);

  // Confirmação de depósito (pix_bancario)
  const [confirmandoDeposito, setConfirmandoDeposito] = useState(null);

  // Edição de data de entrada (pix_bancario)
  const [editandoData, setEditandoData] = useState(false);
  const [novaDataPagamento, setNovaDataPagamento] = useState('');
  const [salvandoData, setSalvandoData] = useState(false);

  // Tipo de Vinculação
  const [tipoVinculacao, setTipoVinculacao] = useState(null); // 'parcela' | 'venda_avista'
  const [sequenciaVendaVinc, setSequenciaVendaVinc] = useState('');
  const [vendaVinculacao, setVendaVinculacao] = useState(null);
  const [loadingVendaVinc, setLoadingVendaVinc] = useState(false);

  useEffect(() => {
    if (abaBaixa) return;
    carregarLinks();
    const timer = setInterval(() => carregarLinks(true), 30000);
    return () => { clearInterval(timer); versaoConsulta.current++; };
  }, [filtros, abaBaixa]);

  const carregarLinks = async (silencioso = false) => {
    const atual = ++versaoConsulta.current;
    if (!silencioso) setLoading(true);
    try {
      const response = await apiService.conciliacao.buscarLinks(filtros);
      if (atual !== versaoConsulta.current) return;
      setLinks(response.data?.data || []);
    } catch (error) {
      if (atual !== versaoConsulta.current) return;
      setLinks(atuais => atuais.map(link => link.baixa_erp ? { ...link, baixa_erp: {
        ...link.baixa_erp, situacao_erp: 'nao_verificado', consultado_em: null,
        erro_associacao: false, parcelas_erp: [], venda_erp: null,
        valor_previsto_erp: null, saldo_aberto_erp: null, diferenca_valor_erp: null,
        motivo_erp: 'Não foi possível atualizar a consulta. Atualize antes de conferir a baixa.'
      } } : link));
      console.error('Erro ao carregar links:', error);
      toast.error('Erro ao carregar links PIX');
    } finally {
      if (atual === versaoConsulta.current) setLoading(false);
    }
  };

  const aplicarFiltrosBusca = () => {
    setFiltros(prev => ({
      ...prev,
      busca: filtroNome.trim() || undefined,
      data_inicio: filtroDataInicio || undefined,
      data_fim: filtroDataFim || undefined,
    }));
  };

  const limparFiltrosBusca = () => {
    setFiltroNome('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setFiltros(prev => {
      const f = { ...prev };
      delete f.busca; delete f.data_inicio; delete f.data_fim;
      return f;
    });
  };

  // Após os outros helpers (temContasInformadas, formatarMoeda, etc.)

  const extrairParcelasDoLink = (link) => {
    // Tenta parcelas_detalhes (da conciliacao_parcelas)
    if (link.parcelas_detalhes && Array.isArray(link.parcelas_detalhes) && link.parcelas_detalhes.length > 0) {
      return link.parcelas_detalhes;
    }
    // Tenta contas_receber (campo JSON gravado ao criar o link)
    if (link.contas_receber) {
      try {
        const parsed = typeof link.contas_receber === 'string'
          ? JSON.parse(link.contas_receber)
          : link.contas_receber;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { /* ignora */ }
    }
    // Tenta ordens_contas
    if (link.ordens_contas) {
      try {
        const parsed = typeof link.ordens_contas === 'string'
          ? JSON.parse(link.ordens_contas)
          : link.ordens_contas;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch { /* ignora */ }
    }
    return [];
  };

  const ehLinkComParcelasConhecidas = (link) => {
    if (!link) return false;
    const ehAleatorio = link.link_aleatorio === true || link.link_aleatorio === 1;
    const ehBancario = link.origem_pix === 'pix_bancario';
    if (ehAleatorio || ehBancario) return false;
    const parcelas = extrairParcelasDoLink(link);
    if (parcelas.length === 0) return false;
    // Só considera "conhecidas" se forem objetos com dados (não inteiros/strings)
    return typeof parcelas[0] === 'object' && parcelas[0] !== null;
  };

  // ─── Modal PIX Bancário ────────────────────────────────────────────────────

  const abrirModalPixBancario = () => {
    setModalPixBancario(true);
    setPbBusca(''); setPbClientes([]); setPbClienteSelecionado(null);
    setPbTipoCobranca('parcela'); setPbSequenciaVenda(''); setPbVendaSelecionada(null);
    setPbContas([]); setPbContasSelecionadas([]);
    setPbValor(''); setPbPagadorNome(''); setPbPagadorCpf('');
    setPbValorJuros(''); setPbTemJuros(false);
    setPbDataPagamento(new Date().toISOString().split('T')[0]);
    setPbObservacoes(''); setPbHorarioDeposito(''); setPbErro(null);

  };

  const fecharModalPixBancario = () => setModalPixBancario(false);

  const pbBuscarClientes = async () => {
    if (!pbBusca.trim()) return;
    setPbLoadingBusca(true);
    setPbClientes([]); setPbClienteSelecionado(null); setPbContas([]);
    try {
      const response = await apiService.clientes.buscar(pbBusca, 10, 0);
      const lista = response.data?.data || response.data || [];
      setPbClientes(Array.isArray(lista) ? lista.slice(0, 10) : []);
    } catch { setPbErro('Erro ao buscar clientes'); }
    finally { setPbLoadingBusca(false); }
  };

  const pbSelecionarCliente = (cliente) => {
    setPbClienteSelecionado(cliente);
    setPbClientes([]);
    if (pbTipoCobranca === 'parcela') pbBuscarContas(cliente.Ordem);
  };

  const pbBuscarContas = async (ordemCliente) => {
    setPbLoadingContas(true);
    setPbContas([]); setPbContasSelecionadas([]);
    try {
      const response = await apiService.clientes.buscarContasConciliacao(ordemCliente, 1);
      const lista = response.data?.data || response.data?.contas || response.data || [];
      setPbContas(Array.isArray(lista) ? lista : []);
    } catch { setPbErro('Erro ao buscar contas'); }
    finally { setPbLoadingContas(false); }
  };

  const pbBuscarVenda = async () => {
    if (!pbSequenciaVenda.trim()) return;
    setPbLoadingVenda(true); setPbVendaSelecionada(null); setPbErro(null);
    try {
      const response = await apiService.vendas.buscarPorSequencia(parseInt(pbSequenciaVenda));
      const dados = response.data || response;
      if (dados?.success && dados?.venda) {
        const v = dados.venda;
        if (!v.Valor_Liquido) v.Valor_Liquido = parseFloat(v.Preco_Final_Somado || 0) - parseFloat(v.Desconto_Valor_Somado || 0);
        setPbVendaSelecionada(v);
        setPbValor(v.Valor_Liquido.toFixed(2));
      } else {
        setPbErro('Venda não encontrada');
      }
    } catch { setPbErro('Erro ao buscar venda'); }
    finally { setPbLoadingVenda(false); }
  };

  const pbToggleConta = (ordem) => {
    setPbContasSelecionadas(prev => {
      const novas = prev.includes(ordem) ? prev.filter(o => o !== ordem) : [...prev, ordem];
      // Recalcular valor automaticamente
      const total = pbContas
        .filter(c => novas.includes(c.Ordem))
        .reduce((s, c) => s + parseFloat(c.Valor_Total || 0), 0);
      setPbValor(total > 0 ? total.toFixed(2) : '');
      setPbValorJuros('');
      setPbTemJuros(false);
      return novas;
    });
  };

  const pbTotalContas = () =>
    pbContas.filter(c => pbContasSelecionadas.includes(c.Ordem))
      .reduce((s, c) => s + parseFloat(c.Valor_Total || 0), 0);

  const pbValorFinal = () => {
    const base = pbTotalContas();
    const juros = parseFloat(pbValorJuros || 0);
    return base + juros;
  };

  const pbSalvar = async () => {
    if (!pbClienteSelecionado && !pbVendaSelecionada) {
      setPbErro('Selecione um cliente ou venda'); return;
    }
    if (!pbValor || parseFloat(pbValor) <= 0) {
      setPbErro('Informe o valor recebido'); return;
    }
    if (!pbPagadorNome.trim()) {
      setPbErro('Informe o nome do pagador conforme extrato'); return;
    }
    if (!pbHorarioDeposito) {
      setPbErro('Informe o horário aproximado do depósito'); return;
    }
    setPbSalvando(true); setPbErro(null);
    try {
      const cliente = pbClienteSelecionado;
      const venda = pbVendaSelecionada;
      const dados = {
        ordem_cliente: cliente?.Ordem || venda?.Ordem_Cli_For,
        codigo_cliente: cliente?.Codigo || venda?.Codigo_Cli_For,
        nome_cliente: cliente?.Nome || venda?.Nome_Cli_For,
        valor: parseFloat(pbValor),
        pagador_nome: pbPagadorNome,
        pagador_cpf_cnpj: null,
        nome_extrato: pbPagadorNome,
        horario_deposito: pbHorarioDeposito || null,
        tipo_pix: pbTipoCobranca,
        contas_receber: pbTipoCobranca === 'parcela' ? pbContasSelecionadas : [],
        sequencia_venda: venda?.Sequencia || null,
        ordem_venda: venda?.Ordem || null,
        data_pagamento: pbDataPagamento || null,
        observacoes: pbObservacoes || null,
        usuario: 'operador'
      };
      const response = await apiService.conciliacao.registrarPixBancario(dados);
      if (response.data?.success) {
        toast.success('✅ PIX bancário registrado com sucesso!');
        fecharModalPixBancario();
        carregarLinks();
      } else {
        setPbErro(response.data?.error || 'Erro ao registrar');
      }
    } catch (error) {
      setPbErro(error.response?.data?.error || 'Erro ao registrar PIX bancário');
    } finally {
      setPbSalvando(false);
    }
  };

  // ─── Confirmação de Depósito (PIX Bancário) ───────────────────────────────

  const confirmarDeposito = async (link) => {
    setConfirmandoDeposito(link.id);
    try {
      const response = await apiService.conciliacao.confirmarDeposito(link.id);
      if (response.data?.success) {
        toast.success('✅ Depósito confirmado!');
        setLinks(prev => prev.map(l =>
          l.id === link.id ? { ...l, deposito_confirmado: 1, deposito_confirmado_em: new Date().toISOString() } : l
        ));
      } else {
        toast.error(response.data?.error || 'Erro ao confirmar depósito');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao confirmar depósito');
    } finally {
      setConfirmandoDeposito(null);
    }
  };

  // ─── Modal de Vinculação ───────────────────────────────────────────────────

  const abrirModalVinculacao = (link) => {
    setLinkSelecionado(link);
    setModalVinculacao(true);
    setBuscaCliente('');
    setClientes([]);
    setClienteSelecionado(null);
    setContasDisponiveis([]);
    setContasSelecionadas([]);
    setObservacoes(link.observacoes_conciliacao || '');
    setValorJuros('');
    setContasExpandido(false);
    setSequenciaVendaVinc('');
    setVendaVinculacao(null);
    setLoadingVendaVinc(false);
    if (link.origem_pix === 'pix_bancario') {
      setTipoVinculacao(link.tipo_pix === 'venda_avista' ? 'venda_avista' : 'parcela');
      if (link.ordem_cliente) buscarClientePorOrdem(link.ordem_cliente, link);
    } else {
      const ehAleatorio = link.link_aleatorio === true || link.link_aleatorio === 1;
      if (ehAleatorio) {
        // Link aleatório — exibir seletor
        setTipoVinculacao(null);
      } else {
        // CobrançaPix — pular seletor, ir direto
        if (link.tipo_pix === 'venda_avista') {
          setTipoVinculacao('venda_avista');
        } else {
          setTipoVinculacao('parcela');
          if (link.ordem_cliente) buscarClientePorOrdem(link.ordem_cliente, link);
        }
      }
    }
  };

  const fecharModalVinculacao = () => {
    setModalVinculacao(false);
    setLinkSelecionado(null);
    setBuscaCliente('');
    setClientes([]);
    setClienteSelecionado(null);
    setContasDisponiveis([]);
    setContasSelecionadas([]);
    setObservacoes('');
    setValorJuros('');
    setTipoVinculacao(null);
    setSequenciaVendaVinc('');
    setVendaVinculacao(null);
    setEditandoData(false);
    setNovaDataPagamento('');
  };

  const salvarDataPagamento = async () => {
    if (!novaDataPagamento || !linkSelecionado) return;
    setSalvandoData(true);
    try {
      await apiService.conciliacao.atualizarDataPagamento(linkSelecionado.id, novaDataPagamento);
      const novaData = novaDataPagamento + 'T12:00:00';
      setLinkSelecionado(prev => ({ ...prev, data_pagamento: novaData }));
      setLinks(prev => prev.map(l => l.id === linkSelecionado.id ? { ...l, data_pagamento: novaData } : l));
      setEditandoData(false);
      setNovaDataPagamento('');
      toast.success('Data atualizada!');
    } catch (err) {
      toast.error('Erro ao atualizar data');
    } finally {
      setSalvandoData(false);
    }
  };

  const buscarClientePorOrdem = async (ordem, linkAtual = null) => {
    try {
      const response = await apiService.clientes.buscarPorOrdem(ordem);
      const cliente = response.data?.data || response.data;
      if (cliente) {
        setClienteSelecionado(cliente);
        buscarContasCliente(cliente.Ordem, linkAtual); // ← passa link
      }
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
    }
  };

  const buscarClientes = async () => {
    if (!buscaCliente.trim()) {
      toast.error('Digite um nome ou código para buscar');
      return;
    }
    setLoadingBusca(true);
    setClientes([]);
    setClienteSelecionado(null);
    setContasDisponiveis([]);
    setContasSelecionadas([]);
    try {
      const response = await apiService.clientes.buscar(buscaCliente, 10, 0);
      let clientesEncontrados = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          clientesEncontrados = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          clientesEncontrados = response.data.data;
        }
      }
      if (clientesEncontrados.length === 0) {
        toast.error('Nenhum cliente encontrado');
      } else {
        setClientes(clientesEncontrados.slice(0, 10));
        if (clientesEncontrados.length === 1) selecionarCliente(clientesEncontrados[0]);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      toast.error('Erro ao buscar clientes');
    } finally {
      setLoadingBusca(false);
    }
  };

  const selecionarCliente = (cliente) => {
    setClienteSelecionado(cliente);
    setClientes([]);
    buscarContasCliente(cliente.Ordem);
  };

  const buscarContasCliente = async (ordemCliente, linkAtual = null) => {
    const link = linkAtual || linkSelecionado;
    setLoadingContas(true);
    setContasDisponiveis([]);
    setContasSelecionadas([]);
    try {
      // ⭐ Tenta extrair ordens já conhecidas (crediário gerado OU pix bancário com contas salvas)
      let ordensConhecidas = [];

      if (link) {
        // 1) Tenta contas_receber / ordens_contas com inteiros (não objetos)
        const parsed = extrairParcelasDoLink(link);
        if (parsed.length > 0 && typeof parsed[0] !== 'object') {
          ordensConhecidas = parsed.map(o => parseInt(o)).filter(Boolean);
        }

        // 2) Para PIX bancário, também tenta ordens_contas diretamente
        if (ordensConhecidas.length === 0 && link.origem_pix === 'pix_bancario' && link.ordens_contas) {
          try {
            const oc = typeof link.ordens_contas === 'string'
              ? JSON.parse(link.ordens_contas)
              : link.ordens_contas;
            if (Array.isArray(oc) && oc.length > 0 && typeof oc[0] !== 'object') {
              ordensConhecidas = oc.map(Number).filter(Boolean);
            }
          } catch { /* ignora */ }
        }
      }

      if (ordensConhecidas.length > 0) {
        // Busca só as contas específicas pelo array de ordens
        const response = await apiService.clientes.buscarContasPorOrdens(ordensConhecidas);
        const contas = response.data?.data || [];
        if (contas.length > 0) {
          setContasDisponiveis(contas);
          setContasExpandido(true);
          setContasSelecionadas(ordensConhecidas);
        } else {
          toast.error('Conta(s) não encontrada(s) no ERP');
        }
        return;
      }

      // Fluxo normal — aleatório ou bancário sem ordens gravadas: busca todas as contas abertas
      const response = await apiService.clientes.buscarContasConciliacao(ordemCliente, 1);
      let contasEncontradas = [];
      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          contasEncontradas = response.data.data;
        } else if (response.data.contas && Array.isArray(response.data.contas)) {
          contasEncontradas = response.data.contas;
        } else if (Array.isArray(response.data)) {
          contasEncontradas = response.data;
        }
      }
      if (contasEncontradas.length > 0) {
        setContasDisponiveis(contasEncontradas);
        setContasExpandido(true);
      } else {
        toast.error('Nenhuma conta a receber encontrada');
      }
    } catch (error) {
      console.error('Erro ao buscar contas:', error);
      toast.error('Erro ao buscar contas a receber');
    } finally {
      setLoadingContas(false);
    }
  };
  
  const toggleSelecaoConta = (ordem) => {
    setContasSelecionadas(prev =>
      prev.includes(ordem) ? prev.filter(o => o !== ordem) : [...prev, ordem]
    );
  };

  const calcularTotalSelecionado = () => {
    return contasDisponiveis
      .filter(conta => contasSelecionadas.includes(conta.Ordem))
      .reduce((total, conta) => total + parseFloat(conta.Valor_Total || 0), 0);
  };

  const vincularContasSelecionadas = async () => {
    if (contasSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma conta');
      return;
    }
    const totalSelecionado = calcularTotalSelecionado();
    const valorPago = parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0);
    const diferenca = parseFloat((valorPago - totalSelecionado).toFixed(2));
    if (totalSelecionado > valorPago + 0.10) {
      toast.error(`Total das contas (${formatarMoeda(totalSelecionado)}) é maior que o valor PIX (${formatarMoeda(valorPago)}). Remova algumas contas.`);
      return;
    }
    if (diferenca > 0.10) {
      const juros = parseFloat(valorJuros || 0);
      if (juros <= 0) {
        toast.error(`Faltam ${formatarMoeda(diferenca)} para fechar o valor PIX. Informe os juros.`);
        return;
      }
      const totalComJuros = parseFloat((totalSelecionado + juros).toFixed(2));
      if (Math.abs(totalComJuros - valorPago) > 0.10) {
        toast.error(`Contas + Juros = ${formatarMoeda(totalComJuros)}, mas o PIX é ${formatarMoeda(valorPago)}. Corrija os juros.`);
        return;
      }
    }
    try {
      setLoadingBusca(true);
      const contasSelecionadasDetalhes = contasDisponiveis
        .filter(c => contasSelecionadas.includes(c.Ordem))
        .map(c => ({
          ordem_conta: c.Ordem,
          valor_parcela: parseFloat(c.Valor_Total || 0),
          valor_vinculado: parseFloat(c.Valor_Total || 0),
          data_vencimento: c.Data_Vencimento,
          dias_atraso: c.Dias_Atraso || 0
        }));
      const response = await apiService.conciliacao.vincularContas(
        linkSelecionado.id,
        contasSelecionadas,
        observacoes,
        parseFloat(valorJuros || 0),
        contasSelecionadasDetalhes
      );
      if (response.data.success) {
        toast.success(`✅ ${contasSelecionadas.length} conta(s) vinculada(s)!`);
        fecharModalVinculacao();
        carregarLinks();
      } else {
        toast.error(response.data.error || 'Erro ao vincular contas');
      }
    } catch (error) {
      console.error('Erro ao vincular:', error);
      toast.error(error.response?.data?.error || 'Erro ao vincular contas');
    } finally {
      setLoadingBusca(false);
    }
  };

  const buscarVendaVinculacao = async () => {
    if (!sequenciaVendaVinc.trim()) return;
    setLoadingVendaVinc(true);
    setVendaVinculacao(null);
    try {
      const response = await apiService.vendas.buscarPorSequencia(parseInt(sequenciaVendaVinc));
      const dados = response.data || response;
      if (dados?.success && dados?.venda) {
        const v = dados.venda;
        if (!v.Valor_Liquido) v.Valor_Liquido = parseFloat(v.Preco_Final_Somado || 0) - parseFloat(v.Desconto_Valor_Somado || 0);
        setVendaVinculacao(v);
      } else {
        toast.error(`Venda #${sequenciaVendaVinc} não encontrada`);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error;
      if (msg) {
        toast.error(msg);
      } else {
        toast.error(`Venda #${sequenciaVendaVinc} não encontrada`);
      }
    } finally {
      setLoadingVendaVinc(false);
    }
  };

  const vincularVenda = async () => {
    const vendaDados = vendaVinculacao || (linkSelecionado?.sequencia_venda ? {
      Sequencia: linkSelecionado.sequencia_venda,
      Ordem: linkSelecionado.ordem_venda,
      Valor_Liquido: parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0),
      Data: linkSelecionado.data_pagamento || new Date().toISOString(),
    } : null);
    if (!vendaDados) return;
    try {
      setLoadingBusca(true);
      const response = await apiService.conciliacao.vincularContas(
        linkSelecionado.id,
        [],
        observacoes,
        0,
        [{
          ordem_conta: null,
          tipo: 'venda_avista',
          sequencia_venda: vendaDados.Sequencia,
          ordem_venda: vendaDados.Ordem,
          valor_parcela: vendaDados.Valor_Liquido,
          valor_vinculado: parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0),
          data_vencimento: vendaDados.Data,
          dias_atraso: 0
        }]
      );
      if (response.data.success) {
        toast.success('✅ Venda vinculada!');
        fecharModalVinculacao();
        carregarLinks();
      } else {
        toast.error(response.data.error || 'Erro ao vincular venda');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao vincular venda');
    } finally {
      setLoadingBusca(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const temContasInformadas = (link) => {
    if (link.sequencia_venda) return true;
    if (link.parcelas_detalhes && Array.isArray(link.parcelas_detalhes) && link.parcelas_detalhes.length > 0) return true;
    if (!link.contas_receber) return false;
    try {
      const contas = typeof link.contas_receber === 'string' ? JSON.parse(link.contas_receber) : link.contas_receber;
      return Array.isArray(contas) && contas.length > 0;
    } catch { return false; }
  };

  const formatarMoeda = (valor) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);

  const formatarTelefone = (telefone) => {
    if (!telefone) return '';
    const numero = telefone.replace(/\D/g, '');
    if (numero.length === 11) return numero.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (numero.length === 10) return numero.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return numero;
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') buscarClientes(); };

  // ─── Badges ───────────────────────────────────────────────────────────────

  const renderPagamentoBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />Pago
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        {status}
      </span>
    );
  };

  const renderConciliacaoBadge = (status_conciliacao) => {
    const configs = {
      'pago_pendente':   { cor: 'bg-yellow-100 text-yellow-800', icone: Clock,        texto: 'Aguardando Conciliação' },
      'pago_em_analise': { cor: 'bg-orange-100 text-orange-800', icone: AlertTriangle, texto: 'Em Análise' },
      'pago_conciliado': { cor: 'bg-blue-100 text-blue-800',     icone: CheckCircle,   texto: 'Conciliado' },
      'erro_pagamento':  { cor: 'bg-red-100 text-red-800',       icone: XCircle,       texto: 'Erro' },
    };
    const config = configs[status_conciliacao] || configs['pago_pendente'];
    const Icone = config.icone;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.cor}`}>
        <Icone className="w-3 h-3 mr-1" />{config.texto}
      </span>
    );
  };

  const renderOrigemBadge = (origem, linkAleatorio) => {
    if (linkAleatorio) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Zap className="w-3 h-3 mr-1" />Link Aleatório
        </span>
      );
    }
    if (origem === 'pix_bancario') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Building2 className="w-3 h-3 mr-1" />PIX Bancário
        </span>
      );
    }
    return null;
  };

  const renderBadgeAtraso = (conta) => {
  // Quitado: Situacao = 'Q' ou Dias_Atraso = -1
    if (conta.Situacao === 'Q' || conta.Dias_Atraso === -1) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <CheckCircle className="w-3 h-3 mr-1" />Recebido
        </span>
      );
    }
    if (conta.Dias_Atraso > 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3 mr-1" />Atraso {conta.Dias_Atraso}d
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />Em dia
      </span>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2"><h1 className="text-2xl font-bold">Conciliação PIX</h1><AjudaConciliacao /></div>
            <p className="text-purple-100 mt-1">Vincule pagamentos PIX às contas a receber</p>
          </div>
          {!abaBaixa && <button
            onClick={abrirModalPixBancario}
            className="px-4 py-2 bg-white text-purple-600 font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center space-x-2"
          >
            <Banknote className="w-5 h-5" />
            <span>Gerar PIX Bancário</span>
          </button>}
        </div>
      </div>

      {podeBaixar && <div className="flex flex-wrap gap-2" aria-label="Etapas da conciliação PIX">
        <button onClick={() => setParams({})} className={`px-4 py-2 rounded-lg ${!abaBaixa ? 'bg-purple-600 text-white' : 'bg-white border'}`}>Identificação e vínculos</button>
        <button onClick={() => setParams({ aba: 'baixa' })} className={`px-4 py-2 rounded-lg ${abaBaixa ? 'bg-purple-600 text-white' : 'bg-white border'}`}>Aguardando baixa</button>
      </div>}
      {abaBaixa ? <BaixaPix /> : <>
      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <Filter className="w-5 h-5 text-gray-600 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 flex-shrink-0">Conciliação:</span>
          <div className="flex space-x-2 flex-wrap gap-2">
            <button
              onClick={() => setFiltros(prev => { const f = {...prev}; delete f.status_conciliacao; return f; })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!filtros.status_conciliacao ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Todos
            </button>
            <button
              onClick={() => setFiltros(prev => ({ ...prev, status_conciliacao: ['pago_pendente', 'pago_em_analise'] }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${JSON.stringify(filtros.status_conciliacao) === JSON.stringify(['pago_pendente', 'pago_em_analise']) ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Aguardando Conciliação
            </button>
            <button
              onClick={() => setFiltros(prev => ({ ...prev, status_conciliacao: ['pago_conciliado'] }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${JSON.stringify(filtros.status_conciliacao) === JSON.stringify(['pago_conciliado']) ? 'bg-blue-100 text-blue-800 border-2 border-blue-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Conciliados
            </button>
          </div>
        </div>

        {/* Linha de busca por nome e data */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <input
              type="text"
              value={filtroNome}
              onChange={e => setFiltroNome(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && aplicarFiltrosBusca()}
              placeholder="Buscar por nome do cliente ou pagador..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={filtroDataInicio}
              onChange={e => setFiltroDataInicio(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              title="Data início"
            />
            <span className="text-gray-400 text-sm">até</span>
            <input
              type="date"
              value={filtroDataFim}
              onChange={e => setFiltroDataFim(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              title="Data fim"
            />
          </div>
          <button
            onClick={aplicarFiltrosBusca}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-1"
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
          {(filtroNome || filtroDataInicio || filtroDataFim) && (
            <button
              onClick={limparFiltrosBusca}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium flex items-center gap-1"
            >
              <XCircle className="w-4 h-4" />
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Lista de Links */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <span className="ml-3 text-gray-600">Carregando...</span>
        </div>
      ) : links.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhum link encontrado</p>
        </div>
      ) : (
        <div className="space-y-4">
          {links.map((link) => (
            <div key={link.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <User className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {link.nome_cliente || 'Cliente não identificado'}
                    </h3>
                    {renderPagamentoBadge(link.status)}
                    {renderConciliacaoBadge(link.status_conciliacao)}
                    {renderOrigemBadge(link.origem_pix, link.link_aleatorio)}
                  </div>
                  <p className="text-sm text-gray-500">
                    Código: {link.codigo_cliente} | PIX ID: #{link.id}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-600">
                    {formatarMoeda(link.valor_pago || link.valor)}
                  </p>
                  {link.origem_pix === 'pix_bancario' ? (
                    <div className="text-xs text-gray-500 space-y-0.5 mt-1">
                      <p><span className="font-medium">Lançamento:</span> {format(new Date(link.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
                      <p><span className="font-medium">Entrada na conta:</span> {link.data_pagamento ? format(new Date(link.data_pagamento.slice(0,10) + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) : <span className="text-yellow-600">não informado</span>}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {format(new Date(link.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Contas a Receber:</p>
                {temContasInformadas(link) ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <p className="text-sm text-green-800">
                      {link.sequencia_venda && !(link.parcelas_detalhes?.length || link.parcelas_vinculadas)
                        ? `✅ Venda à Vista #${link.sequencia_venda}`
                        : `✅ ${link.parcelas_detalhes?.length || link.parcelas_vinculadas || 0} conta(s) vinculada(s)`}
                    </p>
                    {link.status_conciliacao !== 'pago_conciliado' && (
                      link.origem_pix === 'pix_bancario' ? (
                        link.deposito_confirmado ? (
                          <span className="px-4 py-2 bg-blue-100 text-blue-800 text-sm font-medium rounded-lg flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4" /><span>Depósito Confirmado</span>
                          </span>
                        ) : (
                          <button
                            onClick={() => confirmarDeposito(link)}
                            disabled={confirmandoDeposito === link.id}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                          >
                            {confirmandoDeposito === link.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <CheckCircle className="w-4 h-4" />}
                            <span>Confirmar Depósito</span>
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => abrirModalVinculacao(link)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <Link className="w-4 h-4" /><span>Editar</span>
                        </button>
                      )
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
                    <p className="text-sm text-yellow-800">⚠️ Não informado</p>
                    {link.status_conciliacao !== 'pago_conciliado' && (
                      <button
                        onClick={() => abrirModalVinculacao(link)}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
                      >
                        <Link className="w-4 h-4" /><span>Vincular</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {link.baixa_erp && <BaixaPixVinculado dados={link.baixa_erp} onAtualizar={() => carregarLinks(true)} />}

              {link.observacoes_conciliacao && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-700 mb-1">Observações:</p>
                  <p className="text-sm text-gray-600">{link.observacoes_conciliacao}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      </>}

      {/* ═══ MODAL PIX BANCÁRIO ════════════════════════════════════════════════ */}
      {modalPixBancario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <Banknote className="w-6 h-6 mr-2" />Registrar PIX Bancário
                </h3>
                <button onClick={fecharModalPixBancario} className="text-white hover:text-gray-200">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <p className="text-purple-100 text-sm mt-1">
                PIX recebido diretamente na conta bancária — sem link gerado pelo sistema
              </p>
            </div>

            <div className="p-6 space-y-6">
              {pbErro && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /><span>{pbErro}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Referência</label>
                <div className="flex space-x-3">
                  {[['parcela', 'Parcela do Crediário'], ['venda_avista', 'Venda à Vista']].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => {
                        setPbTipoCobranca(val);
                        setPbClienteSelecionado(null); setPbContas([]); setPbContasSelecionadas([]);
                        setPbVendaSelecionada(null); setPbSequenciaVenda(''); setPbValor('');
                      }}
                      className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${pbTipoCobranca === val ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {pbTipoCobranca === 'parcela' && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                  <h4 className="text-sm font-medium text-gray-900 flex items-center">
                    <Search className="w-4 h-4 mr-2" />Buscar Cliente (devedor)
                  </h4>
                  <div className="flex space-x-2">
                    <select value={pbTipoBusca} onChange={e => setPbTipoBusca(e.target.value)} className="w-28 px-2 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="nome">Nome</option>
                      <option value="codigo">Código</option>
                    </select>
                    <input
                      type={pbTipoBusca === 'codigo' ? 'number' : 'text'}
                      value={pbBusca}
                      onChange={e => setPbBusca(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && pbBuscarClientes()}
                      placeholder="Buscar..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button onClick={pbBuscarClientes} disabled={pbLoadingBusca} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm flex items-center space-x-1 disabled:opacity-50">
                      {pbLoadingBusca ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      <span>Buscar</span>
                    </button>
                  </div>
                  {pbClientes.length > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                      {pbClientes.map(c => (
                        <button key={c.Ordem} onClick={() => pbSelecionarCliente(c)} className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center justify-between text-sm">
                          <div>
                            <p className="font-medium">{c.Nome}</p>
                            <p className="text-gray-500 text-xs">Código: {c.Codigo}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  )}
                  {pbClienteSelecionado && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{pbClienteSelecionado.Nome}</p>
                        <p className="text-xs text-gray-500">Código: {pbClienteSelecionado.Codigo}</p>
                      </div>
                      <button onClick={() => { setPbClienteSelecionado(null); setPbContas([]); setPbContasSelecionadas([]); }} className="text-purple-600 text-xs hover:underline">Trocar</button>
                    </div>
                  )}
                  {pbLoadingContas && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                      <span className="ml-2 text-sm text-gray-600">Carregando parcelas...</span>
                    </div>
                  )}
                  {pbContas.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-2">Selecione a(s) parcela(s) paga(s):</p>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {pbContas.map(conta => (
                          <label key={conta.Ordem} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input type="checkbox" checked={pbContasSelecionadas.includes(conta.Ordem)} onChange={() => pbToggleConta(conta.Ordem)} className="h-4 w-4 text-purple-600 mr-3" />
                            <div className="flex-1 flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-700">
                                  Venc: {format(new Date(conta.Data_Vencimento), 'dd/MM/yyyy', { locale: ptBR })}
                                </span>
                                {renderBadgeAtraso(conta)}
                              </div>
                              <span className="font-semibold">{formatarMoeda(conta.Valor_Total)}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                      {pbContasSelecionadas.length > 0 && (
                        <p className="text-xs text-purple-700 mt-1 font-medium">Total selecionado: {formatarMoeda(pbTotalContas())}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {pbTipoCobranca === 'venda_avista' && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Buscar Venda por Sequência</h4>
                  <div className="flex space-x-2">
                    <input type="number" value={pbSequenciaVenda} onChange={e => setPbSequenciaVenda(e.target.value)} onKeyPress={e => e.key === 'Enter' && pbBuscarVenda()} placeholder="Nº da sequência..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    <button onClick={pbBuscarVenda} disabled={pbLoadingVenda} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm flex items-center space-x-1 disabled:opacity-50">
                      {pbLoadingVenda ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      <span>Buscar</span>
                    </button>
                  </div>
                  {pbVendaSelecionada && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                      <p className="font-medium text-green-800">✅ Venda #{pbVendaSelecionada.Sequencia}</p>
                      <p className="text-green-700">Cliente: {pbVendaSelecionada.Nome_Cli_For}</p>
                      <p className="text-green-700">Valor: {formatarMoeda(pbVendaSelecionada.Valor_Liquido)}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor das Parcelas (R$)
                  </label>
                  <input
                    type="text"
                    value={pbTotalContas() > 0 ? `R$ ${pbTotalContas().toFixed(2).replace('.', ',')}` : '—'}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data em que o PIX entrou na conta *</label>
                  <input type="date" value={pbDataPagamento} onChange={e => setPbDataPagamento(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              {/* Juros */}
              {pbTotalContas() > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pbTemJuros}
                      onChange={e => {
                        setPbTemJuros(e.target.checked);
                        if (!e.target.checked) {
                          setPbValorJuros('');
                          setPbValor(pbTotalContas().toFixed(2));
                        }
                      }}
                      className="h-4 w-4 text-orange-600"
                    />
                    <span className="text-sm font-medium text-orange-800">Cliente pagou juros / multa?</span>
                  </label>
                  {pbTemJuros && (
                    <div className="space-y-2">
                      <div className="flex gap-3 items-center">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-orange-900 mb-1">Valor dos Juros (R$)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={pbValorJuros}
                            onChange={e => {
                              setPbValorJuros(e.target.value);
                              const base = pbTotalContas();
                              const juros = parseFloat(e.target.value || 0);
                              setPbValor((base + juros).toFixed(2));
                            }}
                            placeholder="0,00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-orange-900 mb-1">Valor Total a Receber</label>
                          <input
                            type="text"
                            value={`R$ ${pbValorFinal().toFixed(2).replace('.', ',')}`}
                            readOnly
                            className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm bg-orange-50 font-semibold text-orange-900 cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {!pbTemJuros && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-orange-700">Valor total a receber:</span>
                      <span className="font-semibold text-orange-900">R$ {pbTotalContas().toFixed(2).replace('.', ',')}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-blue-900 flex items-center">
                  <User className="w-4 h-4 mr-2" />Dados do Pagador (conforme extrato bancário)
                </h4>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nome no Extrato *</label>
                  <input type="text" value={pbPagadorNome} onChange={e => setPbPagadorNome(e.target.value)} placeholder="Nome exibido no extrato..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                 <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Horário do Depósito *</label>
                  <input type="time" value={pbHorarioDeposito} onChange={e => setPbHorarioDeposito(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações (opcional)</label>
                <textarea value={pbObservacoes} onChange={e => setPbObservacoes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" placeholder="Ex: cliente pagou em nome do cônjuge..." />
              </div>

              <div className="flex space-x-3">
                <button onClick={fecharModalPixBancario} className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg">Cancelar</button>
                <button
                  onClick={pbSalvar}
                  disabled={pbSalvando || !pbPagadorNome.trim() || !pbHorarioDeposito || (pbTipoCobranca === 'parcela' ? (pbTotalContas() <= 0 || !pbClienteSelecionado) : !pbVendaSelecionada)}
                  className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pbSalvando ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Registrando...</span></> : <><Banknote className="w-4 h-4" /><span>Registrar PIX Bancário</span></>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL DE VINCULAÇÃO ══════════════════════════════════════════════ */}
      {modalVinculacao && linkSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">

            {/* Cabeçalho */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-800">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white flex items-center">
                    <Link className="w-6 h-6 mr-2" />Vincular Contas a Receber ao Pagamento PIX
                  </h3>
                  <p className="text-purple-100 mt-1 text-sm">
                    Cliente do PIX: {linkSelecionado.nome_cliente} | Valor Pago: {formatarMoeda(linkSelecionado.valor_pago || linkSelecionado.valor)}
                  </p>
                </div>
                <button onClick={fecharModalVinculacao} className="text-white hover:text-gray-200 transition-colors">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Corpo */}
            <div className="p-6">

              {/* STEP 0: Escolha do tipo */}
              {!tipoVinculacao ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 text-center mb-6">
                    O que está sendo pago neste PIX de{' '}
                    <strong>{formatarMoeda(linkSelecionado.valor_pago || linkSelecionado.valor)}</strong>?
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => {
                        setTipoVinculacao('parcela');
                        if (linkSelecionado.ordem_cliente) buscarClientePorOrdem(linkSelecionado.ordem_cliente);
                      }}
                      className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-colors"
                    >
                      <FileText className="w-10 h-10 text-purple-600" />
                      <div className="text-center">
                        <p className="font-semibold text-gray-900">Parcela do Crediário</p>
                        <p className="text-xs text-gray-500 mt-1">Pagamento de conta a receber em aberto</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setTipoVinculacao('venda_avista')}
                      className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-colors"
                    >
                      <DollarSign className="w-10 h-10 text-purple-600" />
                      <div className="text-center">
                        <p className="font-semibold text-gray-900">Venda à Vista</p>
                        <p className="text-xs text-gray-500 mt-1">Pagamento de uma venda no caixa</p>
                      </div>
                    </button>
                  </div>
                  <button onClick={fecharModalVinculacao} className="w-full mt-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg">
                    Cancelar
                  </button>
                </div>

              ) : tipoVinculacao === 'venda_avista' ? (
                /* STEP: Venda à Vista */
                <div className="space-y-5">
                  {(linkSelecionado?.link_aleatorio === true || linkSelecionado?.link_aleatorio === 1) && (
                    <button
                      onClick={() => { setTipoVinculacao(null); setClienteSelecionado(null); setContasDisponiveis([]); setContasSelecionadas([]); }}
                      className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                    >
                      ← Voltar
                    </button>
                  )}
                  {/* PIX bancário venda à vista — com sequencia gravada: somente leitura */}
                  {linkSelecionado?.origem_pix === 'pix_bancario' && linkSelecionado?.sequencia_venda && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-1">
                      <p className="text-sm font-medium text-green-800">✅ Venda registrada ao lançar o PIX Bancário</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Sequência:</span> #{linkSelecionado.sequencia_venda}</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Cliente devedor:</span> {linkSelecionado.nome_cliente}</p>
                      {(linkSelecionado.nome_extrato || linkSelecionado.pagador_nome) && (
                        <p className="text-sm text-green-700"><span className="font-medium">Nome no extrato:</span> <span className="font-bold text-red-600">{linkSelecionado.nome_extrato || linkSelecionado.pagador_nome}</span></p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap text-sm text-green-700">
                        <span className="font-medium">Data de entrada na conta:</span>
                        {editandoData ? (
                          <>
                            <input type="date" value={novaDataPagamento} onChange={e => setNovaDataPagamento(e.target.value)} className="px-2 py-0.5 border border-gray-300 rounded text-sm text-gray-800" />
                            <button onClick={salvarDataPagamento} disabled={salvandoData || !novaDataPagamento} className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs disabled:opacity-50 flex items-center gap-1">
                              {salvandoData ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salvar'}
                            </button>
                            <button onClick={() => { setEditandoData(false); setNovaDataPagamento(''); }} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">Cancelar</button>
                          </>
                        ) : (
                          <>
                            <span className="font-bold text-red-600">{linkSelecionado.data_pagamento ? new Date(linkSelecionado.data_pagamento.slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}{linkSelecionado.horario_deposito ? ` às ${linkSelecionado.horario_deposito}` : ''}</span>
                            <button onClick={() => { setEditandoData(true); setNovaDataPagamento(linkSelecionado.data_pagamento ? linkSelecionado.data_pagamento.slice(0,10) : ''); }} className="text-purple-600 hover:text-purple-800" title="Editar data">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-green-700"><span className="font-medium">Valor:</span> {formatarMoeda(linkSelecionado.valor_pago || linkSelecionado.valor)}</p>
                    </div>
                  )}

                  {/* PIX bancário venda à vista — SEM sequencia gravada: busca manual */}
                  {linkSelecionado?.origem_pix === 'pix_bancario' && !linkSelecionado?.sequencia_venda && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-blue-800 mb-2">
                        <DollarSign className="w-4 h-4 flex-shrink-0" />
                        <span>Informe a sequência da venda à vista para vincular este PIX bancário.</span>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={sequenciaVendaVinc}
                          onChange={e => setSequenciaVendaVinc(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && buscarVendaVinculacao()}
                          placeholder="Nº da sequência da venda..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button onClick={buscarVendaVinculacao} disabled={loadingVendaVinc}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm flex items-center gap-1 disabled:opacity-50">
                          {loadingVendaVinc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          <span>Buscar</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* CobrançaPix normal — com sequencia_venda: resumo somente leitura */}
                  {linkSelecionado?.origem_pix !== 'pix_bancario' &&
                   !(linkSelecionado?.link_aleatorio === true || linkSelecionado?.link_aleatorio === 1) &&
                   linkSelecionado?.sequencia_venda && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-1">
                      <p className="text-sm font-medium text-green-800">✅ Venda vinculada ao gerar o link PIX</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Sequência:</span> #{linkSelecionado.sequencia_venda}</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Cliente:</span> {linkSelecionado.nome_cliente}</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Valor PIX:</span> {formatarMoeda(linkSelecionado.valor_pago || linkSelecionado.valor)}</p>
                      {linkSelecionado.descricao && (
                        <p className="text-sm text-green-700"><span className="font-medium">Descrição:</span> {linkSelecionado.descricao}</p>
                      )}
                    </div>
                  )}

                  {/* CobrançaPix normal — SEM sequencia_venda: busca manual */}
                  {linkSelecionado?.origem_pix !== 'pix_bancario' &&
                   !(linkSelecionado?.link_aleatorio === true || linkSelecionado?.link_aleatorio === 1) &&
                   !linkSelecionado?.sequencia_venda && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-blue-800 mb-2">
                        <DollarSign className="w-4 h-4 flex-shrink-0" />
                        <span>Informe a sequência da venda à vista para vincular este PIX.</span>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={sequenciaVendaVinc}
                          onChange={e => setSequenciaVendaVinc(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && buscarVendaVinculacao()}
                          placeholder="Nº da sequência da venda..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button onClick={buscarVendaVinculacao} disabled={loadingVendaVinc}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm flex items-center gap-1 disabled:opacity-50">
                          {loadingVendaVinc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          <span>Buscar</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Link aleatório — busca manual de venda */}
                  {(linkSelecionado?.link_aleatorio === true || linkSelecionado?.link_aleatorio === 1) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />Buscar Venda por Sequência
                      </h4>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={sequenciaVendaVinc}
                          onChange={e => setSequenciaVendaVinc(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && buscarVendaVinculacao()}
                          placeholder="Nº da sequência da venda..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button onClick={buscarVendaVinculacao} disabled={loadingVendaVinc} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm flex items-center gap-1 disabled:opacity-50">
                          {loadingVendaVinc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          <span>Buscar</span>
                        </button>
                      </div>
                    </div>
                  )}


                  {vendaVinculacao && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-1">
                      <p className="text-sm font-medium text-green-800">✅ Venda Encontrada</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Sequência:</span> #{vendaVinculacao.Sequencia}</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Cliente:</span> {vendaVinculacao.Nome_Cli_For}</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Data:</span> {format(new Date(vendaVinculacao.Data), 'dd/MM/yyyy', { locale: ptBR })}</p>
                      <p className="text-sm text-green-700"><span className="font-medium">Valor:</span> {formatarMoeda(vendaVinculacao.Valor_Liquido)}</p>
                      {Math.abs(parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0) - vendaVinculacao.Valor_Liquido) > 0.01 && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                          Valor da venda ({formatarMoeda(vendaVinculacao.Valor_Liquido)}) difere do PIX ({formatarMoeda(linkSelecionado.valor_pago || linkSelecionado.valor)})
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observações (opcional)</label>
                    <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" placeholder="Ex: pagamento da venda pelo cônjuge..." />
                  </div>

                  <div className="flex gap-3">
                    <button onClick={fecharModalVinculacao} className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg">Cancelar</button>
                    <button
                      onClick={vincularVenda}
                      disabled={(!vendaVinculacao && !linkSelecionado?.sequencia_venda) || loadingBusca}
                      className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingBusca ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Vinculando...</span></> : <><Link className="w-5 h-5" /><span>Vincular Venda</span></>}
                    </button>
                  </div>
                </div>

              ) : (
                /* STEP: Parcela do Crediário */
                <div className="space-y-6">

                  {/* Botão voltar — apenas links aleatórios */}
                  {(linkSelecionado?.link_aleatorio === true || linkSelecionado?.link_aleatorio === 1) && (
                    <button
                      onClick={() => { setTipoVinculacao(null); setClienteSelecionado(null); setContasDisponiveis([]); setContasSelecionadas([]); }}
                      className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                    >
                      ← Voltar
                    </button>
                  )}

                  {/* ── CASO 1: CobrançaPix — parcelas já conhecidas ─────────────────── */}
                  {ehLinkComParcelasConhecidas(linkSelecionado) ? (() => {
                    const parcelas = extrairParcelasDoLink(linkSelecionado);
                    const valorPago = parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0);

                    // Soma das parcelas originais (campo valor_parcela ou Valor_Total ou valor_vinculado)
                    const totalParcelas = parcelas.reduce((s, p) => {
                      const v = parseFloat(p.valor_parcela || p.Valor_Total || p.valor_vinculado || p.valor || 0);
                      return s + v;
                    }, 0);
                    const diferenca = parseFloat((valorPago - totalParcelas).toFixed(2));

                    return (
                      <>
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                          <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          <span>Parcelas vinculadas automaticamente ao gerar o link PIX.</span>
                        </div>

                        {/* Lista somente leitura das parcelas */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-gray-600" />
                            <span className="font-medium text-gray-900">Parcelas deste PIX</span>
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                              {parcelas.length} parcela(s)
                            </span>
                          </div>
                          <div className="divide-y divide-gray-200">
                            {parcelas.map((p, idx) => {
                              const valor = parseFloat(p.valor_parcela || p.Valor_Total || p.valor_vinculado || p.valor || 0);
                              const venc = p.data_vencimento || p.Data_Vencimento;
                              const ordem = p.ordem_conta || p.Ordem;
                              const nota = p.nota || p.Nota || '';
                              return (
                                <div key={ordem || idx} className="flex items-center justify-between p-4">
                                  <div className="flex items-center space-x-3">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-700">
                                        {venc
                                          ? `Vencimento: ${format(new Date(venc), 'dd/MM/yyyy', { locale: ptBR })}`
                                          : `Parcela ${idx + 1}`}
                                      </p>
                                      {nota && <p className="text-xs text-gray-500">Nota: {nota}</p>}
                                      {ordem && <p className="text-xs text-gray-400">Ordem: {ordem}</p>}
                                    </div>
                                  </div>
                                  <span className="text-base font-semibold text-gray-900">{formatarMoeda(valor)}</span>
                                </div>
                              );
                            })}
                          </div>
                          {/* Totais */}
                          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Total parcelas</span>
                            <span className="text-base font-semibold text-gray-900">{formatarMoeda(totalParcelas)}</span>
                          </div>
                        </div>

                        {/* Diferença / juros */}
                        {diferenca > 0.10 && (
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded space-y-3">
                            <div className="flex items-start space-x-2">
                              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-orange-800">
                                Diferença de <strong>{formatarMoeda(diferenca)}</strong> entre o valor PIX ({formatarMoeda(valorPago)}) e as parcelas ({formatarMoeda(totalParcelas)}). Informe os juros se houver.
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-orange-900 mb-1">Juros / Multa / Correção</label>
                              <input
                                type="number" step="0.01" min="0"
                                value={valorJuros}
                                onChange={e => setValorJuros(e.target.value)}
                                placeholder={diferenca.toFixed(2)}
                                className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm"
                              />
                            </div>
                          </div>
                        )}

                        {/* Observações */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Observações (opcional)</label>
                          <textarea
                            value={observacoes}
                            onChange={e => setObservacoes(e.target.value)}
                            rows={2}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none text-sm"
                            placeholder="Ex: cliente pagou em nome de outra pessoa..."
                          />
                        </div>

                        {/* Botões */}
                        <div className="flex space-x-3">
                          <button onClick={fecharModalVinculacao} className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg">
                            Cancelar
                          </button>
                          <button
                            onClick={async () => {
                              const detalhes = parcelas.map(p => ({
                                ordem_conta: p.ordem_conta || p.Ordem,
                                valor_parcela: parseFloat(p.valor_parcela || p.Valor_Total || p.valor_vinculado || p.valor || 0),
                                valor_vinculado: parseFloat(p.valor_parcela || p.Valor_Total || p.valor_vinculado || p.valor || 0),
                                data_vencimento: p.data_vencimento || p.Data_Vencimento,
                                dias_atraso: p.dias_atraso || p.Dias_Atraso || 0
                              }));
                              const ordens = detalhes.map(d => d.ordem_conta).filter(Boolean);

                              const valorPagoLocal = parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0);
                              const totalParcelasLocal = detalhes.reduce((s, d) => s + d.valor_parcela, 0);
                              const diferencaLocal = parseFloat((valorPagoLocal - totalParcelasLocal).toFixed(2));

                              if (diferencaLocal > 0.10) {
                                const juros = parseFloat(valorJuros || 0);
                                if (juros <= 0) {
                                  toast.error(`Faltam ${formatarMoeda(diferencaLocal)} para fechar o valor PIX. Informe os juros.`);
                                  return;
                                }
                                const totalComJuros = parseFloat((totalParcelasLocal + juros).toFixed(2));
                                if (Math.abs(totalComJuros - valorPagoLocal) > 0.10) {
                                  toast.error(`Parcelas + Juros = ${formatarMoeda(totalComJuros)}, mas o PIX é ${formatarMoeda(valorPagoLocal)}. Corrija os juros.`);
                                  return;
                                }
                              }

                              try {
                                setLoadingBusca(true);
                                const response = await apiService.conciliacao.vincularContas(
                                  linkSelecionado.id,
                                  ordens,
                                  observacoes,
                                  parseFloat(valorJuros || 0),
                                  detalhes
                                );
                                if (response.data.success) {
                                  toast.success(`✅ ${detalhes.length} parcela(s) vinculada(s)!`);
                                  fecharModalVinculacao();
                                  carregarLinks();
                                } else {
                                  toast.error(response.data.error || 'Erro ao vincular contas');
                                }
                              } catch (error) {
                                toast.error(error.response?.data?.error || 'Erro ao vincular contas');
                              } finally {
                                setLoadingBusca(false);
                              }
                            }}
                            disabled={loadingBusca}
                            className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {loadingBusca
                              ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Vinculando...</span></>
                              : <><Link className="w-5 h-5" /><span>Confirmar Vinculação</span></>}
                          </button>
                        </div>
                      </>
                    );
                  })() : (
                  /* ── CASO 2: Link aleatório / bancário — seleção manual ─────────── */
                  <>
                    {linkSelecionado?.origem_pix === 'pix_bancario' && (() => {
                      const ordensGravadas = (() => {
                        if (!linkSelecionado.ordens_contas) return [];
                        try {
                          const oc = typeof linkSelecionado.ordens_contas === 'string'
                            ? JSON.parse(linkSelecionado.ordens_contas)
                            : linkSelecionado.ordens_contas;
                          return Array.isArray(oc) ? oc : [];
                        } catch { return []; }
                      })();
                      return ordensGravadas.length > 0 ? (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                            <Building2 className="w-4 h-4" />
                            <span>PIX Bancário — parcelas pré-selecionadas</span>
                          </div>
                          <div className="text-sm text-blue-800 space-y-1">
                            <p><span className="font-medium">Cliente devedor:</span> {linkSelecionado.nome_cliente}</p>
                            {(linkSelecionado.nome_extrato || linkSelecionado.pagador_nome) && (
                              <p><span className="font-medium">Nome no extrato:</span> <span className="font-bold text-red-600">{linkSelecionado.nome_extrato || linkSelecionado.pagador_nome}</span></p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">Data de entrada na conta:</span>
                              {editandoData ? (
                                <>
                                  <input type="date" value={novaDataPagamento} onChange={e => setNovaDataPagamento(e.target.value)} className="px-2 py-0.5 border border-gray-300 rounded text-sm text-gray-800" />
                                  <button onClick={salvarDataPagamento} disabled={salvandoData || !novaDataPagamento} className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs disabled:opacity-50 flex items-center gap-1">
                                    {salvandoData ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salvar'}
                                  </button>
                                  <button onClick={() => { setEditandoData(false); setNovaDataPagamento(''); }} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">Cancelar</button>
                                </>
                              ) : (
                                <>
                                  <span className="font-bold text-red-600">{linkSelecionado.data_pagamento ? new Date(linkSelecionado.data_pagamento.slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}{linkSelecionado.horario_deposito ? ` às ${linkSelecionado.horario_deposito}` : ''}</span>
                                  <button onClick={() => { setEditandoData(true); setNovaDataPagamento(linkSelecionado.data_pagamento ? linkSelecionado.data_pagamento.slice(0,10) : ''); }} className="text-purple-600 hover:text-purple-800" title="Editar data">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                </>
                              )}
                            </div>
                            <p><span className="font-medium">Parcelas:</span> {ordensGravadas.length} ordem(ns) pré-selecionada(s)</p>
                            <p><span className="font-medium">Valor:</span> {formatarMoeda(linkSelecionado.valor_pago || linkSelecionado.valor)}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                            <Building2 className="w-4 h-4 flex-shrink-0" />
                            <span>PIX Bancário. Selecione as parcelas para vincular.</span>
                          </div>
                          <div className="text-sm text-blue-800 space-y-1">
                            {(linkSelecionado.nome_extrato || linkSelecionado.pagador_nome) && (
                              <p><span className="font-medium">Nome no extrato:</span> <span className="font-bold text-red-600">{linkSelecionado.nome_extrato || linkSelecionado.pagador_nome}</span></p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">Data de entrada na conta:</span>
                              {editandoData ? (
                                <>
                                  <input type="date" value={novaDataPagamento} onChange={e => setNovaDataPagamento(e.target.value)} className="px-2 py-0.5 border border-gray-300 rounded text-sm text-gray-800" />
                                  <button onClick={salvarDataPagamento} disabled={salvandoData || !novaDataPagamento} className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs disabled:opacity-50 flex items-center gap-1">
                                    {salvandoData ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salvar'}
                                  </button>
                                  <button onClick={() => { setEditandoData(false); setNovaDataPagamento(''); }} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">Cancelar</button>
                                </>
                              ) : (
                                <>
                                  <span className="font-bold text-red-600">{linkSelecionado.data_pagamento ? new Date(linkSelecionado.data_pagamento.slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}{linkSelecionado.horario_deposito ? ` às ${linkSelecionado.horario_deposito}` : ''}</span>
                                  <button onClick={() => { setEditandoData(true); setNovaDataPagamento(linkSelecionado.data_pagamento ? linkSelecionado.data_pagamento.slice(0,10) : ''); }} className="text-purple-600 hover:text-purple-800" title="Editar data">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Busca de Cliente */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <Search className="w-4 h-4 mr-2 text-gray-600" />Buscar Cliente
                      </h4>
                      <div className="flex space-x-4">
                        <div className="w-32">
                          <select value={tipoBusca} onChange={e => setTipoBusca(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                            <option value="nome">Nome</option>
                            <option value="codigo">Código</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <input
                            type={tipoBusca === 'codigo' ? 'number' : 'text'}
                            value={buscaCliente}
                            onChange={e => setBuscaCliente(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={tipoBusca === 'nome' ? 'Digite o nome...' : 'Digite o código...'}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <button onClick={buscarClientes} disabled={loadingBusca} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center space-x-2 disabled:opacity-50">
                          {loadingBusca ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Buscando...</span></> : <><Search className="w-5 h-5" /><span>Buscar</span></>}
                        </button>
                      </div>
                      {clientes.length > 0 && (
                        <div className="mt-4 border border-gray-300 rounded-lg divide-y max-h-60 overflow-y-auto">
                          {clientes.map(cliente => (
                            <button key={cliente.Ordem} onClick={() => selecionarCliente(cliente)} className="w-full px-4 py-3 text-left hover:bg-purple-50 flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{cliente.Nome}</p>
                                <p className="text-sm text-gray-500">Código: {cliente.Codigo}</p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Cliente Selecionado */}
                    {clienteSelecionado && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-purple-900">Cliente Selecionado</h4>
                          <button onClick={() => { setClienteSelecionado(null); setContasDisponiveis([]); setContasSelecionadas([]); }} className="text-purple-600 text-sm font-medium">Trocar</button>
                        </div>
                        <p className="font-semibold text-gray-900">{clienteSelecionado.Nome}</p>
                        <p className="text-sm text-gray-600">Código: {clienteSelecionado.Codigo}</p>
                      </div>
                    )}

                    {/* Contas Disponíveis — seleção manual */}
                    {loadingContas ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                        <span className="ml-3 text-gray-600">Carregando contas...</span>
                      </div>
                    ) : contasDisponiveis.length > 0 && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <button onClick={() => setContasExpandido(!contasExpandido)} className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-gray-600" />
                            <span className="font-medium text-gray-900">Contas a Receber Disponíveis</span>
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">{contasDisponiveis.length} conta(s)</span>
                          </div>
                          {contasExpandido ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                        </button>
                        {contasExpandido && (
                          <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                            {contasDisponiveis.map(conta => (
                              <label key={conta.Ordem} className="flex items-start p-4 hover:bg-gray-50 cursor-pointer">
                                <input type="checkbox" checked={contasSelecionadas.includes(conta.Ordem)} onChange={() => toggleSelecaoConta(conta.Ordem)} className="mt-1 h-5 w-5 text-purple-600 border-gray-300 rounded" />
                                <div className="ml-4 flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <Calendar className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm font-medium text-gray-700">
                                        Vencimento: {format(new Date(conta.Data_Vencimento), 'dd/MM/yyyy', { locale: ptBR })}
                                      </span>
                                      {renderBadgeAtraso(conta)}
                                    </div>
                                    <span className="text-base font-semibold text-gray-900">{formatarMoeda(conta.Valor_Total)}</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Nota: {conta.Nota}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Resumo da Seleção */}
                    {contasSelecionadas.length > 0 && (() => {
                      const total = calcularTotalSelecionado();
                      const valorPago = parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0);
                      const diferenca = parseFloat((valorPago - total).toFixed(2));
                      const juros = parseFloat(valorJuros || 0);
                      const totalComJuros = parseFloat((total + juros).toFixed(2));
                      const ok = Math.abs(totalComJuros - valorPago) <= 0.10;
                      return (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                          <h4 className="text-sm font-medium text-purple-900">Resumo da Seleção</h4>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-purple-800">{contasSelecionadas.length} conta(s)</p>
                            <p className="text-lg font-semibold text-purple-900">{formatarMoeda(total)}</p>
                          </div>
                          {total > valorPago + 0.10 && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start space-x-2">
                              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                              <p className="text-sm text-red-800">Total ({formatarMoeda(total)}) maior que o PIX ({formatarMoeda(valorPago)}).</p>
                            </div>
                          )}
                          {diferenca > 0.10 && (
                            <div className="p-3 bg-orange-50 border border-orange-200 rounded space-y-3">
                              <div className="flex items-start space-x-2">
                                <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                                <p className="text-sm text-orange-800">Faltam <strong>{formatarMoeda(diferenca)}</strong> para fechar o PIX. Informe os juros.</p>
                              </div>
                              <input type="number" step="0.01" min="0" value={valorJuros} onChange={e => setValorJuros(e.target.value)} placeholder={diferenca.toFixed(2)} className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm" />
                              {juros > 0 && (
                                <div className={`flex items-center justify-between text-sm font-medium rounded p-2 ${ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  <span>Contas + Juros:</span>
                                  <span>{formatarMoeda(totalComJuros)} {ok ? '✅' : `≠ ${formatarMoeda(valorPago)}`}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {(Math.abs(total - valorPago) <= 0.10 || ok) && total <= valorPago + 0.10 && (
                            <div className="flex items-center space-x-2 text-green-700 text-sm">
                              <CheckCircle className="w-4 h-4" /><span>Valores conferem</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Observações */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Observações (opcional)</label>
                      <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none text-sm" placeholder="Ex: Cliente pagou em nome de outra pessoa..." />
                    </div>

                    {/* Botões */}
                    <div className="flex space-x-3">
                      <button onClick={fecharModalVinculacao} className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg">Cancelar</button>
                      <button
                        onClick={vincularContasSelecionadas}
                        disabled={(() => {
                          if (contasSelecionadas.length === 0 || loadingBusca) return true;
                          const total = calcularTotalSelecionado();
                          const valorPago = parseFloat(linkSelecionado.valor_pago || linkSelecionado.valor || 0);
                          if (total > valorPago + 0.10) return true;
                          const diferenca = parseFloat((valorPago - total).toFixed(2));
                          if (diferenca > 0.10) {
                            const juros = parseFloat(valorJuros || 0);
                            if (Math.abs(parseFloat((total + juros).toFixed(2)) - valorPago) > 0.10) return true;
                          }
                          return false;
                        })()}
                        className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        {loadingBusca ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Vinculando...</span></> : <><Link className="w-5 h-5" /><span>Vincular {contasSelecionadas.length} Conta(s)</span></>}
                      </button>
                    </div>
                  </>
                  )}
                </div>
              )}
           </div>  
          </div>    
        </div>      
      )}            
    </div>          
  );
}